import type { BookmarkChange, BookmarkRow, PushResult, SyncStateRow } from '@bsync/shared';
import type { RemoteApi } from '@bsync/sync-engine';
import type { BsyncClient } from './client';
import { toRemoteError } from './errors';
import { BOOKMARK_COLUMNS, bookmarkFromDb, changeToDb, type DbBookmark } from './mapping';

/** RemoteApi over Supabase: RPCs for writes, PostgREST (RLS-filtered) for pulls. */
export class SupabaseRemote implements RemoteApi {
  constructor(private readonly client: BsyncClient) {}

  async syncState(deviceId: string): Promise<SyncStateRow> {
    const { data, error, status } = await this.client.rpc('sync_state', { p_device_id: deviceId });
    if (error) throw toRemoteError(error, status);
    const d = data as { rev: number | string; gc_rev: number | string };
    return { rev: Number(d.rev), gcRev: Number(d.gc_rev) };
  }

  async push(deviceId: string, changes: BookmarkChange[]): Promise<PushResult> {
    const { data, error, status } = await this.client.rpc('push_changes', {
      p_device_id: deviceId,
      p_changes: changes.map(changeToDb),
    });
    if (error) throw toRemoteError(error, status);
    const d = data as { rows: DbBookmark[]; rejected: PushResult['rejected']; rev: number | string };
    return { rows: d.rows.map(bookmarkFromDb), rejected: d.rejected, rev: Number(d.rev) };
  }

  async pull(sinceRev: number, limit: number): Promise<BookmarkRow[]> {
    const { data, error, status } = await this.client
      .from('bookmarks')
      .select(BOOKMARK_COLUMNS)
      .gt('rev', sinceRev)
      .order('rev', { ascending: true })
      .limit(limit);
    if (error) throw toRemoteError(error, status);
    return (data as unknown as DbBookmark[]).map(bookmarkFromDb);
  }
}
